import React from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash.isequal';
import { financingTermsSelectors } from '../ducks';
import PageTitle from '../../../shared/components/PageTitle';
import LoadingPage from '../../../shared/components/LoadingPage';
import ErrorPage from '../../../shared/components/ErrorPage';
import ShowFinancingTerms from '../ShowFinancingTerms';
import { getPartner, getDifferenceInDays } from '../../../utilities';
import CarvanaLearnFinancing from '../CarvanaLearnFinancing';
import PartnerLearnFinancing from '../PartnerLearnFinancing';
import ExpiredFinancing from '../ExpiredFinancing';
import {
  FINANCING_TITLE,
  LENDING_TREE_PARTNER_ID,
  CREDIT_KARMA_PARTNER_ID,
  CARVANA_PARTNER_ID
} from '../constants';
import { TermsExpire, ExpirationDate } from '../ShowFinancingTerms/ShowFinancingTerms.styles';
import type { RootState } from '../../../store/types';

const areFinancingTermsExpired = (formattedExpirationDate: string) => {
  const isExpirationDateProvided =
    formattedExpirationDate !== '' && formattedExpirationDate !== null;
  if (!isExpirationDateProvided) return false;
  const expirationDifferenceInDays = getDifferenceInDays(formattedExpirationDate);

  return expirationDifferenceInDays < 0;
};

type StateProps = {
  financingTermsError: Record<string, unknown>;
  formattedExpirationDate: string;
  hasFinancingTerms: boolean;
  isLoadingFinancingTerms: boolean;
  termsOverviewError: Record<string, unknown>;
};

function FinancingTerms({
  isLoadingFinancingTerms,
  termsOverviewError,
  financingTermsError,
  hasFinancingTerms,
  formattedExpirationDate
}: StateProps) {
  const { partnerId, partnerLongName, partnerVanityUrlName } = getPartner();
  const pageTitle = FINANCING_TITLE;
  const hasError = !isEqual(financingTermsError, {}) || !isEqual(termsOverviewError, {});
  const shouldShowError = !isLoadingFinancingTerms && hasError;
  const financingTermsExpired = areFinancingTermsExpired(formattedExpirationDate);
  const shouldShowComponent =
    !isLoadingFinancingTerms && !shouldShowError && hasFinancingTerms && !financingTermsExpired;
  const shouldShowExpiredFinancingComponent =
    !isLoadingFinancingTerms && !shouldShowError && hasFinancingTerms && financingTermsExpired;
  const shouldShowFinancingComponent =
    !isLoadingFinancingTerms && !shouldShowError && !hasFinancingTerms;
  const hasPartnerUsingCarvanaLearnFinancing =
    partnerId === CARVANA_PARTNER_ID ||
    partnerId === CREDIT_KARMA_PARTNER_ID ||
    partnerId === LENDING_TREE_PARTNER_ID;
  const shouldShowPartnerFinancing =
    shouldShowFinancingComponent && !hasPartnerUsingCarvanaLearnFinancing;
  const shouldShowCarvanaLearnFinancing =
    shouldShowFinancingComponent && hasPartnerUsingCarvanaLearnFinancing;
  const expirationDifferenceInDays = getDifferenceInDays(formattedExpirationDate);
  const isExpirationNear = expirationDifferenceInDays >= 0 && expirationDifferenceInDays <= 10;
  const financingExpirationVerbiage = isExpirationNear
    ? 'Your terms are about to expire on '
    : 'Your terms will expire on ';

  return (
    <>
      {!isLoadingFinancingTerms && (
        <PageTitle title={pageTitle} className="financingPageTitle">
          {hasFinancingTerms && !shouldShowError && (
            <TermsExpire>
              {financingExpirationVerbiage}{' '}
              <ExpirationDate>{formattedExpirationDate}</ExpirationDate>.
            </TermsExpire>
          )}
        </PageTitle>
      )}
      {isLoadingFinancingTerms && <LoadingPage />}
      {shouldShowError && <ErrorPage />}
      {shouldShowExpiredFinancingComponent && <ExpiredFinancing />}
      {shouldShowComponent && <ShowFinancingTerms />}
      {shouldShowPartnerFinancing && (
        <PartnerLearnFinancing
          partnerLongName={partnerLongName}
          partnerVanityUrlName={partnerVanityUrlName}
          partnerId={partnerId}
        />
      )}
      {shouldShowCarvanaLearnFinancing && <CarvanaLearnFinancing />}
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  isLoadingFinancingTerms: financingTermsSelectors.getIsLoadingFinancingTerms(state),
  financingTermsError: financingTermsSelectors.getFinancingTermsError(state),
  termsOverviewError: financingTermsSelectors.getTermsOverviewError(state),
  hasFinancingTerms: financingTermsSelectors.getHasFinancingTerms(state),
  formattedExpirationDate: financingTermsSelectors.getFormattedExpirationDate(state)
});

export default connect(mapStateToProps)(FinancingTerms);
